import { Alert, Box, Chip, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import {
  AccumulationLimitType,
  ClockStatus,
  GetWasteContainerQuery,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

import { deriveRuleSource } from "./clockRuleSource";
import {
  formatContainerDate,
  formatContainerDateLong,
  formatDaysRemaining,
  NO_VALUE_PLACEHOLDER,
} from "./containerDisplay";
import { EmptySectionState, SectionCard } from "./containerSections";
import { WasteContainerClockChip } from "./WasteContainerClockChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type AccumulationLimitRow = WasteContainerDetailData["clock"]["limits"][number];

/**
 * The engine's own verdict that this container's clock could not be
 * determined, with which generator-status axes it is waiting on. The
 * resolver only sets it while the clock actually runs (placed, not archived,
 * not shipped), and never infers it from an empty limits list — a resolved
 * VSQG facility legitimately produces zero limits too (VSQG central has no
 * federal TIME rule; VSQG satellite is exempt from the SAA volume limit).
 */
function undeterminedCallout(container: WasteContainerDetailData) {
  return container.clock.undetermined ?? null;
}

function AccumulationLimitRows({ limits }: { limits: AccumulationLimitRow[] }) {
  return (
    <Stack spacing={1.5}>
      {limits.map((limit, index) => (
        <AccumulationLimitRow key={`${limit.limitType}-${index}`} limit={limit} />
      ))}
    </Stack>
  );
}

/**
 * One non-governing limit, stacked rather than tabulated.
 *
 * A six-column table was the obvious shape and the wrong one: these cards are
 * half the drawer wide, and a volume limit has no deadline and no days
 * remaining, so two columns sat empty while `basis` wrapped a word per line and
 * the status chip pushed past the card's edge. Stacking lets each limit say
 * only what it has, in the same order the dial above says it.
 */
function AccumulationLimitRow({ limit }: { limit: AccumulationLimitRow }) {
  const source = deriveRuleSource(limit);
  const timing = [
    limit.deadlineDate ? `Due ${formatContainerDate(limit.deadlineDate)}` : null,
    limit.daysRemaining != null ? formatDaysRemaining(limit.daysRemaining) : null,
  ].filter(Boolean);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2">{limit.basis}</Typography>
        <Chip size="small" variant="outlined" color={source.color} label={source.label} />
        <WasteContainerClockChip status={limit.status} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {[limit.citation, ...timing].filter(Boolean).join(" · ")}
      </Typography>
    </Box>
  );
}

/**
 * The governing time limit as a ring: how much of the window is gone, and how
 * much is left in the middle.
 *
 * Only the soonest dated TIME limit gets the ring — the one actually driving
 * the deadline. Everything else the engine emits is a coinciding obligation
 * (the volume cap, its move window, a state's own satellite clock) and lists
 * underneath: `clock.limits` carries only limits that APPLY — a superseded
 * residence clock (episodic vs standing) is resolved engine-side, and a
 * federal-plus-state pair cannot occur.
 */
function AccumulationClockDial({ limit }: { limit: AccumulationLimitRow }) {
  const theme = useTheme();
  const total = limit.totalDays ?? null;
  const remaining = limit.daysRemaining ?? null;
  const used = total !== null && remaining !== null ? Math.min(total, total - remaining) : null;
  // Overdue keeps the ring full rather than wrapping past it, which would read
  // as progress starting over.
  const fraction = total && used !== null ? Math.max(0, Math.min(1, used / total)) : null;
  const overdue = remaining !== null && remaining < 0;
  const ringColor = overdue
    ? theme.palette.error.main
    : limit.status === ClockStatus.Approaching
      ? theme.palette.warning.main
      : theme.palette.success.main;

  return (
    <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box sx={{ position: "relative", width: 148, height: 148, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={148}
          thickness={4}
          sx={{ color: "grey.300", position: "absolute", inset: 0 }}
        />
        {fraction !== null && (
          <CircularProgress
            variant="determinate"
            value={fraction * 100}
            size={148}
            thickness={4}
            sx={{ color: ringColor, position: "absolute", inset: 0 }}
          />
        )}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ position: "absolute", inset: 0, textAlign: "center" }}
        >
          <Typography variant="h5" sx={{ lineHeight: 1, fontWeight: 600 }}>
            {remaining === null ? NO_VALUE_PLACEHOLDER : Math.abs(remaining)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {remaining === null ? "" : overdue ? "days overdue" : "days left"}
          </Typography>
        </Stack>
      </Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Deadline
          </Typography>
          <Typography variant="body2">{formatContainerDateLong(limit.deadlineDate)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            On site
          </Typography>
          <Typography variant="body2">
            {used !== null && total !== null
              ? `${used} of ${total} days`
              : formatDaysRemaining(limit.daysRemaining)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Basis
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">{limit.basis}</Typography>
            <Chip
              size="small"
              variant="outlined"
              color={deriveRuleSource(limit).color}
              label={deriveRuleSource(limit).label}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {limit.citation}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export function AccumulationClockSection({ container }: { container: WasteContainerDetailData }) {
  const { clock } = container;

  let body: ReactNode;
  // The soonest dated limit governs; it gets the ring, and the coinciding
  // rest stay listed (see AccumulationClockDial's doc).
  const timeLimits = clock.limits
    .filter((limit) => limit.limitType === AccumulationLimitType.Time && limit.deadlineDate)
    .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0));
  const governing = timeLimits[0];
  const others = clock.limits.filter((limit) => limit !== governing);
  const undetermined = undeterminedCallout(container);
  if (governing) {
    body = (
      <Stack spacing={3}>
        <AccumulationClockDial limit={governing} />
        {others.length > 0 && <AccumulationLimitRows limits={others} />}
      </Stack>
    );
  } else if (clock.limits.length > 0) {
    body = <AccumulationLimitRows limits={clock.limits} />;
  } else if (undetermined) {
    // Warning, not info: an undetermined clock means a real deadline may be
    // running unseen. The reason is the engine's own composition — the rules
    // that declined to answer say what they were missing — rendered as given.
    body = <Alert severity="warning">{undetermined.reason}</Alert>;
  } else {
    body = <EmptySectionState message="No accumulation clock applies to this container." />;
  }

  // No compliance badge here: the ring already says the deadline has passed,
  // in red, with the number of days on it. A chip repeating that was the
  // loudest thing on the card and the specs don't draw one. The findings
  // themselves still surface on the compliance dashboard.
  return <SectionCard title="Accumulation clock">{body}</SectionCard>;
}
